import { setTimeout } from 'timers';

import { useState } from 'react';

import * as Sentry from '@sentry/react';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { ErrorText } from 'src/app/components/Elements/ErrorText/ErrorText';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';
import {
  createLeadAction,
  emailCheckV2Captcha,
  removeOnboardingStep,
  segmentTrack,
  setOnboardingFormError,
  setPreviousOnboardingStep,
} from 'src/app/state/actions';
import { getOnboardingForm } from 'src/app/state/selectors';
import { useSelector, useDispatch } from 'src/app/state/store';

import { STEP_KEY } from '../../StepLayout/consts';
import { Step } from '../../StepLayout/Step';
import { setOnboardingStorage } from '../../util';
import { SignInLink } from '../ContactStep/ContactStep';

interface StepHandler {
  handleNextClick: () => void;
}

export const CaptchaStep = ({ handleNextClick }: StepHandler) => {
  useSegmentTrack(UNIFIED.viewReCaptchaFallback);

  const title = 'Please help us protect your security';
  const grecaptcha = window?.grecaptcha;

  const dispatch = useDispatch();
  const { email, zipCode } = useSelector(getOnboardingForm);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const recaptchaCallback = async (captchaToken: string) => {
    try {
      await dispatch(emailCheckV2Captcha(email, captchaToken));
      dispatch(segmentTrack(UNIFIED.captchaSuccessful, { type: 'Interactive' }));
      dispatch(segmentTrack(UNIFIED.createLead));
      dispatch(createLeadAction({ zipCode, email, source: 'web_onboarding_form' }));
      setOnboardingStorage({ email, zipCode });
      dispatch(segmentTrack(UNIFIED.createEligibleLead));
      handleNextClick();
      dispatch(removeOnboardingStep(STEP_KEY.CAPTCHA));
    } catch (error: any) {
      if (error.response?.status === 400) {
        dispatch(setOnboardingFormError(<SignInLink />));
        //email taken, kick user back to email step with error
        //debounce
        setTimeout(() => {
          dispatch(setPreviousOnboardingStep());
          //if for some reason they pass v3 captcha next time, don't send
          //them back here
          dispatch(removeOnboardingStep(STEP_KEY.CAPTCHA));
        }, 500);
      }
      if (error.response?.status === 403) {
        //user could not pass v2 captcha, meaning we think they are a bot
        //show an error message and let them click the back button
        //but basically abandon them. death to bots.
        dispatch(segmentTrack(UNIFIED.captchaFailed, { type: 'Interactive' }));
        setErrorMessage('There was a problem validating your captcha response');
      }
    }
  };
  if (grecaptcha) {
    grecaptcha.ready(() => {
      try {
        grecaptcha.render('recaptcha', {
          sitekey: '6LfNYVkfAAAAAN08gPmPublj7Jr-C2ZZc0YW0R67',
          callback: recaptchaCallback,
        });
      } catch (error: any) {
        /* ignore error thrown by re-rendering recaptcha */
        if (error.toString() !== 'Error: reCAPTCHA has already been rendered in this element') {
          Sentry.captureException(error);
        }
      }
    });
  }
  return (
    <Step title={title}>
      <div id="recaptcha" className="g-recaptcha" data-sitekey="6LfNYVkfAAAAAN08gPmPublj7Jr-C2ZZc0YW0R67" />
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </Step>
  );
};
